import { type DragEvent, useMemo, useState } from 'react';

import { ClearRounded, PushPin, PushPinOutlined, Search } from '@mui/icons-material';
import { Box, ButtonBase, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material';

import { ActivityMetadataModel } from '../../../api/api';
import { usePersistedSetting } from '../../../common/hooks/usePersistedSetting';
import { useIsMobile } from '@@common';
import ActivityIcon from '../Components/ActivityIcon';

const PINNED_GROUPS_STORAGE_KEY = 'flowDesigner.activityGallery.pinnedGroups';
const DEFAULT_PINNED_GROUPS = ['Agentic AI', 'Control Flow'];

type ActivityGalleryProps = {
	activities: ActivityMetadataModel[];
	onSelect: (activity: ActivityMetadataModel) => void;
};

export default function ActivityGallery({ activities, onSelect }: ActivityGalleryProps) {
	const [searchText, setSearchText] = useState('');
	const [pinnedGroups, setPinnedGroups] = usePersistedSetting<string[]>(PINNED_GROUPS_STORAGE_KEY, DEFAULT_PINNED_GROUPS);
	const isMobile = useIsMobile();

	const handleDragStart = (event: DragEvent<HTMLButtonElement>, activity: ActivityMetadataModel) => {
		event.dataTransfer.effectAllowed = 'copy';
		event.dataTransfer.setData('application/x-randol-flow-activity', activity.name);
		event.dataTransfer.setData('text/plain', activity.name);
	};

	const groupedActivities = useMemo(() => {
		const normalizedSearchText = searchText.trim().toLowerCase();
		const matches = activities.filter((activity) => {
			if (!normalizedSearchText) {
				return true;
			}

			return [activity.displayName, activity.description, activity.category].some((value) => value?.toLowerCase().includes(normalizedSearchText));
		});

		return matches.reduce<Record<string, ActivityMetadataModel[]>>((groups, activity) => {
			const groupName = activity.category || 'Other';
			(groups[groupName] ??= []).push(activity);
			return groups;
		}, {});
	}, [activities, searchText]);

	const pinnedGroupNames = useMemo(() => new Set(pinnedGroups), [pinnedGroups]);
	const groupNames = useMemo(
		() =>
			Object.keys(groupedActivities).sort((left, right) => {
				const pinOrder = Number(pinnedGroupNames.has(right)) - Number(pinnedGroupNames.has(left));
				return pinOrder || left.localeCompare(right);
			}),
		[groupedActivities, pinnedGroupNames],
	);
	const toggleGroupPinned = (groupName: string) => {
		setPinnedGroups((currentPinnedGroups) =>
			currentPinnedGroups.includes(groupName)
				? currentPinnedGroups.filter((pinnedGroupName) => pinnedGroupName !== groupName)
				: [...currentPinnedGroups, groupName],
		);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', gap: 1.5, p: 1.5 }}>
			<TextField
				size="small"
				fullWidth
				placeholder="Search activities"
				value={searchText}
				onChange={(event) => setSearchText(event.target.value)}
				slotProps={{
					input: {
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
						endAdornment: searchText ? (
							<InputAdornment position="end">
								<Tooltip title="Clear search">
									<IconButton aria-label="Clear activity search" size="small" onClick={() => setSearchText('')} edge="end">
										<ClearRounded fontSize="small" />
									</IconButton>
								</Tooltip>
							</InputAdornment>
						) : undefined,
					},
				}}
			/>

			<Box sx={{ overflowY: 'auto', minHeight: 0, pr: 0.5 }}>
				{groupNames.map((groupName) => (
					<Box key={groupName} sx={{ '& + &': { mt: 2 } }}>
						<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 28, mb: 0.25 }}>
							<Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
								{groupName}
							</Typography>
							<Tooltip title={pinnedGroupNames.has(groupName) ? `Unpin ${groupName}` : `Pin ${groupName} to the top`}>
								<IconButton
									size="small"
									aria-label={pinnedGroupNames.has(groupName) ? `Unpin ${groupName}` : `Pin ${groupName}`}
									onClick={() => toggleGroupPinned(groupName)}
									color={pinnedGroupNames.has(groupName) ? 'primary' : 'default'}
									sx={{ width: 22, height: 22, p: 0.25 }}
								>
									{pinnedGroupNames.has(groupName) ? <PushPin sx={{ fontSize: 16 }} /> : <PushPinOutlined sx={{ fontSize: 16 }} />}
								</IconButton>
							</Tooltip>
						</Box>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
								gap: 0.75,
								'@media (max-width: 260px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
							}}
						>
							{groupedActivities[groupName].map((activity) => (
								<Tooltip key={activity.name} title={activity.description || activity.displayName} placement="left">
									<ButtonBase
										onClick={() => onSelect(activity)}
										draggable={!isMobile}
										onDragStart={(event) => handleDragStart(event, activity)}
										aria-label={`Add ${activity.displayName}`}
										sx={{
											minWidth: 0,
											minHeight: 86,
											p: 1,
											border: '1px solid',
											borderColor: 'divider',
											borderRadius: 1.5,
											backgroundColor: 'background.paper',
											display: 'flex',
											flexDirection: 'column',
											justifyContent: 'center',
											alignItems: 'center',
											gap: 0.75,
											textAlign: 'center',
											transition: 'border-color 150ms ease, background-color 150ms ease, transform 150ms ease, box-shadow 150ms ease',
											'&:hover': {
												borderColor: 'primary.main',
												backgroundColor: 'action.hover',
												boxShadow: 2,
												transform: 'translateY(-1px)',
											},
											'&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
										}}
									>
										<ActivityIcon activityMetadata={activity} showTriggerBadge maxWidth="2.25rem" />
										<Typography
											variant="caption"
											sx={{
												maxWidth: '100%',
												fontWeight: 600,
												lineHeight: 1.2,
												display: '-webkit-box',
												WebkitLineClamp: 2,
												WebkitBoxOrient: 'vertical',
												overflow: 'hidden',
											}}
										>
											{activity.displayName}
											{activity.categories?.includes('Beta') && <sup style={{ marginLeft: 3, opacity: 0.6 }}>beta</sup>}
										</Typography>
									</ButtonBase>
								</Tooltip>
							))}
						</Box>
					</Box>
				))}
				{groupNames.length === 0 && (
					<Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
						No activities match your search.
					</Typography>
				)}
			</Box>
		</Box>
	);
}
